// Step 1: Client Selection - Search and select existing client or create new one
import { useState } from 'react'
import { useVisitWizard } from '../../../../contexts/VisitWizardContext'
import { useClients, useCreateClient } from '../../../../hooks/useClients'
import { Button } from '../../../../components/ui/button'
import { Input } from '../../../../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../components/ui/dialog'
import { Search, Plus, Loader2 } from 'lucide-react'
import { ClientListItem, Client, ClientCreate } from '../../../../types/client'
import { QuickClientForm } from '../../../../components/forms/QuickClientForm'
import { toast } from 'sonner'

export function ClientSelection() {
  const { selectClient } = useVisitWizard()
  const [search, setSearch] = useState('')
  const [gender, setGender] = useState<string>('all')
  const [isAddingClient, setIsAddingClient] = useState(false)
  const [page, setPage] = useState(1)

  // Build filters
  const filters = {
    ...(search && { search }),
    ...(gender && gender !== 'all' && { gender }),
  }

  // Fetch clients with current filters
  const { data, isLoading, refetch } = useClients(filters, { page, limit: 10 })

  // Create client mutation
  const createClient = useCreateClient()

  const handleSelectClient = (clientListItem: ClientListItem) => {
    // Convert ClientListItem to Client format
    const client: Client = {
      ...clientListItem,
      date_updated: clientListItem.date_created,
    } as Client
    selectClient(client)
  }

  const handleCreateClient = async (clientData: ClientCreate) => {
    try {
      const newClient = await createClient.mutateAsync(clientData)
      toast.success('Client added successfully!')
      setIsAddingClient(false)
      // Refetch the list to show the new client
      refetch()
      // Auto-select the newly created client
      selectClient(newClient)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add client')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-primary">Select Client</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Search for an existing client or add a new one
          </p>
        </div>

        <Dialog open={isAddingClient} onOpenChange={setIsAddingClient}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 gap-2">
              <Plus className="h-4 w-4" />
              Add New Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Client</DialogTitle>
            </DialogHeader>
            <QuickClientForm
              onSubmit={handleCreateClient}
              onCancel={() => setIsAddingClient(false)}
              isLoading={createClient.isPending}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="pl-10"
          />
        </div>

        <Select value={gender} onValueChange={(value) => {
          setGender(value)
          setPage(1)
        }}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by Gender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Genders</SelectItem>
            <SelectItem value="Male">Male</SelectItem>
            <SelectItem value="Female">Female</SelectItem>
            <SelectItem value="Others">Others</SelectItem>
          </SelectContent>
        </Select>

        {(search || gender !== 'all') && (
          <Button
            variant="outline"
            onClick={() => {
              setSearch('')
              setGender('all')
              setPage(1)
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Client Table */}
      <div className="border rounded-lg">
        {isLoading ? (
          <div className="flex justify-center items-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : data && data.items.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell className="font-medium">
                      {client.firstname} {client.middlename ? `${client.middlename} ` : ''}{client.lastname}
                    </TableCell>
                    <TableCell>{client.age || 'N/A'}</TableCell>
                    <TableCell>{client.gender || 'N/A'}</TableCell>
                    <TableCell>{client.city || 'N/A'}</TableCell>
                    <TableCell>{client.contact_number || 'N/A'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleSelectClient(client)}
                        className="bg-primary hover:bg-primary/90"
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {data.total > 10 && (
              <div className="flex items-center justify-between p-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, data.total)} of {data.total} clients
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(data.total / 10)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <p className="text-lg font-medium text-muted-foreground mb-2">
              No clients found
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {search || gender !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first client'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
